import State from "@/helpers/pattern/state";
import { Talento } from "@/models/talento";
import { modelIsModified } from "@/helpers/vue-mc";
import talentoAPI from "@/services/talento";
import CombosAPI from "@/services/catalogos";
import { clearSwal } from "@/constants/sweet-alerts";
import { setSelectsInactiveData, setSelectsInactiveDataMultipleJson } from "@/helpers/form-common";
import { TLT_CONTACTO_NUM_NEXION, TLT_ORIGENCV_NUM_NEXION, TLT_ORIGENCV_ENCRIPTED } from "@/constants/operativos";

/**
 * State Pattern for talento form
 */
export default class TalentoStates extends State {
  /**
   * General Methods
   */
  base() {
    return {
      clear({ $setState }) {
        $setState("initial");
      }
      , async askForClear({ $data }) {
        try {
          if ((await $data.$swal(clearSwal)).value) {
            $data.croppaRemove();
            this.clear();
          }
        } catch (error) {
          console.error(error);
        }
      }
      , resetModel({ $data }) {
        $data.talento = new Talento();
        $data.refreshkey += 1;
      }
      , async setDefaults({ $data }) {
        await this.loadSelectsData();

        let attributes = {};
        attributes.origencv = TLT_ORIGENCV_NUM_NEXION;

        if ($data.$route.query.org) {
          const origen = $data.$route.query.org;
          console.log("origen encriptado: " + origen);
          attributes.origencv = TLT_ORIGENCV_ENCRIPTED[origen] ? TLT_ORIGENCV_ENCRIPTED[origen] : TLT_ORIGENCV_NUM_NEXION;
          console.log("origen: " + attributes.origencv);
          $data.$router.replace("/talento");
        }

        const ipinfo = await talentoAPI.ipinfo();
        if (ipinfo && ipinfo.data) {
          const pais = $data.view.elem.select.pais.filteredList.find((value) => value.codigo.toUpperCase() == ipinfo.data.country.toUpperCase());
          if (pais) {
            attributes.pais = pais;
            await this.loadSelectProvincia(pais);
            const provincia = $data.view.elem.select.provincia.filteredList.find((value) => value.name.toUpperCase() == ipinfo.data.region.toUpperCase());
            if (provincia) {
              attributes.provincia = provincia.code;
              await this.loadSelectLocalidad(provincia.code);
              const localidad = $data.view.elem.select.localidad.filteredList.find((value) => value.name.toUpperCase() == ipinfo.data.city.toUpperCase());
              if (localidad) {
                attributes.localidad = localidad.code;
              }
            }
          }
        }

        $data.talento = new Talento(attributes, null, {});
      }
      , resetValidators({ $data }) {
        $data.$v.$reset();
        $data.refreshkey += 1;
      }
      , isDisableButtonSubmit({ $data }) {
        return $data.hasErrors($data.$v.talento) || $data.view.action.submit.busy || !modelIsModified($data.talento);
      }
      , resetPond({ $data }) {
        try {
          if ($data.$refs.adjuntos) {
            if ($data.$refs.adjuntos.$refs && $data.$refs.adjuntos.$refs.pond) {
              $data.$refs.adjuntos.$refs.pond.removeFiles();
            }
            $data.$refs.adjuntos.myFiles = [];
          }

          if ($data.$refs.video){
            if ($data.$refs.video.$refs && $data.$refs.video.$refs.pond_video) {
              $data.$refs.video.$refs.pond_video.removeFiles();
            }
            $data.$refs.video.myFiles = [];
          }
        } catch (error) {
          console.error(error);
        }
      }
      , async loadImage({ $data }, foto) {
        try {
          let image = new Image();

          // Notice: it's necessary to set "crossorigin" attribute before "src" attribute.
          image.crossOrigin = "Anonymous";
          image.src = foto;

          let orientation = 1;
          image.onload = () => {
            $data.croppa._onload(image, orientation);
          };
          if ($data.croppa.refresh) await $data.croppa.refresh();
        } catch (error) {
          console.log(error);
        }
      }
      , async loadSelectsData({ $data }, idiomas = $data.talento.idiomas) {
        await this.loadSelectPais();
        this.loadSelectPerfiles();
        setTimeout(() => {
          if ($data.$refs.idiomas) $data.$refs.idiomas.loadSelectsData(idiomas);
        }, 900);
      }
      , async loadSelectPais({ $data }) {
        const { data: { results: { combo: paisList } } } = await CombosAPI.get({ option: "pais" });
        $data.view.elem.select.pais.filteredList = paisList ? paisList.filter((value) => value.status) : [];
        $data.view.elem.select.pais.completeList = paisList || [];
      }
      , async loadSelectProvincia({ $data }, pais = $data.talento.pais) {
        if (pais) {
          const { data: { results: { combo: provinciaList } } } = await CombosAPI.getByParameter({ option: "provincia", parameter: pais.code });
          $data.view.elem.select.provincia.filteredList = provinciaList ? provinciaList.filter((value) => value.status) : [];
          $data.view.elem.select.provincia.completeList = provinciaList || [];
          $data.talento.moneda = pais.codigo.toUpperCase();
          $data.$refs.telefonos.setLada(pais.lada);
        }
      }
      , async loadSelectLocalidad({ $data }, provincia = $data.talento.provincia) {
        if (provincia) {
          const { data: { results: { combo: localidadList } } } = await CombosAPI.getByParameter({ option: "localidad", parameter: provincia });
          $data.view.elem.select.localidad.filteredList = localidadList ? localidadList.filter((value) => value.status) : [];
          $data.view.elem.select.localidad.completeList = localidadList || [];
        }
      }
      , async loadSelectPerfiles({ $data }) {
        const { data: { results: { combo: perfilesList } } } = await CombosAPI.get({ option: "perfilTecnico" });
        $data.view.elem.select.perfiles.filteredList = perfilesList ? perfilesList.filter((value) => value.status) : [];
        $data.view.elem.select.perfiles.completeList = perfilesList || [];
      }
      , setValueOrDefault(_data, val) {
        return val && val.length > 0 ? JSON.stringify(val).replace("{}", "") : null;
      }
      , getValueOrDefault(_data, val) {
        return val ? JSON.parse(val) : [];
      }
    };
  }

  /**
   * States and their methods
   */
  states() {
    return {
      initial: {
        $constructor: function ({ $data }) {
          // Reset Models and validators
          this.resetModel();
          this.resetPond();
          this.setDefaults();
          this.resetValidators();

          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.save;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.save;

          $data.captcha = false;
          $data.croppainput = true;
        }
        , async submit(
          { $data },
          {
            nombre, email, linkedin, pais, provincia, localidad, cv, origencv, observacionesgen, perfiles, clientes, telefonos, usuarios, idiomas, adjuntos, video, foto, titulouni, rangoSalarial 
          }
        ) {
          origencv = origencv || TLT_ORIGENCV_NUM_NEXION;

          try {
            const response = await talentoAPI.save({
              clave: null
              , nombre
              , email
              , linkedin
              , pais: pais ? Number(pais.code) : null
              , provincia: pais && provincia ? Number(provincia) : null
              , localidad: pais && provincia && localidad ? Number(localidad) : null
              , contacto: TLT_CONTACTO_NUM_NEXION
              , moneda: ""
              , rangoSalarial
              , sueldoPretendido: 0.0
              , sueldoNegociado: 0.0
              , tipoSalario: ""
              , disponibilidad: ""
              , cv: adjuntos && adjuntos != "" ? true : cv
              , origencv: adjuntos && adjuntos != "" ? origencv : ""
              , cvnxn: false
              , reclutadores: "nsolutions"
              , calificacion: 0
              , seniority: null
              , status: 0
              , observaciones: ""
              , observacionesgen
              , fechaCambioEstatus: ""
              , fechaUltimoContacto: ""
              , fechaContratacion: ""
              , fechaBaja: ""
              , perfiles: this.setValueOrDefault(perfiles)
              , clientes: this.setValueOrDefault(clientes)
              , telefonos: this.setValueOrDefault(telefonos)
              , usuarios: this.setValueOrDefault(usuarios)
              , idiomas: this.setValueOrDefault(idiomas)
              , adjuntos: [...(adjuntos ? adjuntos.split(",").map(item => item.trim()).filter(item => item) : []), ...(video ? [video.trim()] : [])].join(",")
              , foto
              , titulouni
              , mailPCCounter: 0
              , version: 0
            });

            $data.update = true;
            $data.view.action.submit.busy = false;
            this.resetModel();
            this.resetValidators();
            $data.$emit("refreshData");
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
      , editing: {
        $constructor: function ({ $data }, {
          clave, nombre, email, linkedin, pais, provincia, localidad, perfiles, telefonos, usuarios, idiomas, foto, titulouni, rangoSalarial, version 
        }) {
          // Reset Models and validators
          this.resetPond();

          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.update;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.update;

          // Set options to combos if not are active
          if (pais) setSelectsInactiveData(pais, $data.view.elem.select.pais.completeList, $data.view.elem.select.pais.filteredList);
          if (pais && provincia) setSelectsInactiveData(provincia, $data.view.elem.select.provincia.completeList, $data.view.elem.select.provincia.filteredList);
          if (pais && provincia && localidad) setSelectsInactiveData(localidad, $data.view.elem.select.localidad.completeList, $data.view.elem.select.localidad.filteredList);
          $data.view.elem.select.perfiles.filteredList = setSelectsInactiveDataMultipleJson(perfiles, $data.view.elem.select.perfiles.completeList);

          setTimeout(() => {
            $data.talento = new Talento({
              clave
              , nombre
              , email: email ? email.split(",").map(e => e.trim()).join(", ") : ""
              , linkedin
              , pais: pais ? $data.view.elem.select.pais.filteredList.find((value) => value.code == Number(pais)) : null
              , provincia: pais && provincia ? Number(provincia) : null
              , localidad: pais && provincia && localidad ? Number(localidad) : null
              , contacto: ""
              , moneda: ""
              , rangoSalarial
              , sueldoPretendido: 0.0
              , sueldoNegociado: 0.0
              , tipoSalario: ""
              , disponibilidad: ""
              , cv: false
              , origencv: ""
              , cvnxn: false
              , reclutadores: "nsolutions"
              , calificacion: 0
              , seniority: null
              , status: 0
              , observaciones: ""
              , observacionesgen: ""
              , fechaCambioEstatus: ""
              , fechaUltimoContacto: ""
              , fechaContratacion: ""
              , fechaBaja: ""
              , perfiles: this.getValueOrDefault(perfiles)
              , clientes: []
              , telefonos: this.getValueOrDefault(telefonos)
              , usuarios: this.getValueOrDefault(usuarios)
              , idiomas: this.getValueOrDefault(idiomas)
              , adjuntos: ""
              , video: ""
              , foto: foto && foto != "" ? foto : "/static/img/user_thumbnail.jpg"
              , titulouni
              , mailPCCounter: 0
              , version
            });

            this.resetValidators();
          }, 100);
        }
        , async submit(
          { $data },
          {
            clave,
            nombre,
            email,
            linkedin,
            pais,
            provincia,
            localidad,
            contacto,
            moneda,
            rangoSalarial,
            sueldoPretendido,
            sueldoNegociado,
            tipoSalario,
            disponibilidad,
            cv,
            origencv,
            cvnxn,
            reclutadores,
            calificacion,
            seniority,
            status,
            observaciones,
            observacionesgen,
            fechaCambioEstatus,
            fechaUltimoContacto,
            fechaContratacion,
            fechaBaja,
            perfiles,
            clientes,
            telefonos,
            usuarios,
            idiomas,
            adjuntos,
            video,
            foto,
            titulouni,
            mailPCCounter,
            version
          }
        ) {
          try {
            $data.view.action.submit.busy = true;

            origencv = origencv || TLT_ORIGENCV_NUM_NEXION;

            const response = await talentoAPI.update({
              clave
              , nombre
              , email: email ? email.split(",").map(e => e.trim()).join(",") : null 
              , linkedin
              , pais: pais ? Number(pais.code) : null
              , provincia: pais && provincia ? Number(provincia) : null
              , localidad: pais && provincia && localidad ? Number(localidad) : null
              , contacto
              , moneda
              , rangoSalarial
              , sueldoPretendido: Number(sueldoPretendido)
              , sueldoNegociado: Number(sueldoNegociado)
              , tipoSalario
              , disponibilidad
              , cv: adjuntos && adjuntos != "" ? true : cv
              , origencv: adjuntos && adjuntos != "" ? origencv : ""
              , cvnxn
              , reclutadores
              , calificacion: Number(calificacion)
              , seniority: Number(seniority)
              , status
              , observaciones
              , observacionesgen
              , fechaCambioEstatus
              , fechaUltimoContacto
              , fechaContratacion
              , fechaBaja
              , perfiles: this.setValueOrDefault(perfiles)
              , clientes: this.setValueOrDefault(clientes)
              , telefonos: this.setValueOrDefault(telefonos)
              , usuarios: this.setValueOrDefault(usuarios)
              , idiomas: this.setValueOrDefault(idiomas)
              , adjuntos: [...(adjuntos ? adjuntos.split(",").map(item => item.trim()).filter(item => item) : []), ...(video ? [video.trim()] : [])].join(",")
              , foto
              , titulouni
              , mailPCCounter
              , version
            });

            $data.update = true;
            $data.view.action.submit.busy = false;
            this.resetModel();
            this.resetValidators();
            $data.$emit("refreshData");
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/views/talento/talento-state.js